import type { ClientCommunityWithLevel } from "api-types/communities/communityBaseTypes";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import type { ClientLotHydrated } from "api-types/lots/lotsResponses";
import type { UserNotificationType } from "common/types/userNotification";
import { ensureDefined } from "common/utils/assertOfType";
import type { EffectiveLocationAddress } from "lib/components/builders/communities/utils/locationUtils";
import type { SetStateAction } from "react";
import { createContext, useContext } from "react";
import type { LotFilterRow } from "./lotsTypes";

interface LotAction {
  type: LotActionType;
  payload?: Record<string, any>;
}

interface ResetAction extends LotAction {
  type: "reset";
  payload: ClientLotHydrated[];
}

interface UpsertAction extends LotAction {
  type: "upsert";
  payload: {
    rowId: LotRowID;
    fieldName: string;
    fieldValue: string | number | FilterValuesType;
  };
}

interface InsertAction extends LotAction {
  type: "insert";
  payload: {
    lot: LotRowData;
  };
}

interface InsertMultipleAction extends LotAction {
  type: "insert_multiple";
  payload: {
    lots: LotRowData[];
  };
}

interface RemoveLotAction extends LotAction {
  type: "remove_one";
  payload: {
    rowId: LotRowID;
  };
}

interface RemoveSelectedAction extends LotAction {
  type: "remove_selected";
  payload: {
    activeRowId?: LotRowID;
  };
}

interface RemoveAllAction extends LotAction {
  type: "remove_all";
}

interface ToggleSelectAllAction extends LotAction {
  type: "toggle_select_all";
  payload: {
    selected: boolean;
    locationId: string;
    rowIds?: LotRowID[];
  };
}

interface SliceEndAction extends LotAction {
  type: "slice_end";
  payload: {
    startIdx: number;
  };
}

interface FilterUpsertAction extends LotAction {
  type: "filter_upsert";
  payload: {
    name: string;
    valuesArray: string[];
  };
}

interface FiltersClearAllAction extends LotAction {
  type: "clear_all";
}

/** The one multi-row write. Callers resolve which rows the edit applies to. */
interface UpdateRowsAction extends LotAction {
  type: "update_rows";
  payload: {
    rowIds: LotRowID[];
    fieldName: keyof LotRowData;
    fieldValue: string | number | boolean | Date | null | FilterValuesType;
  };
}

interface RemoveRowsAction extends LotAction {
  type: "remove_rows";
  payload: {
    rowIds: LotRowID[];
  };
}

export type LotsReducerActionType =
  | ResetAction
  | InsertAction
  | InsertMultipleAction
  | UpsertAction
  | RemoveLotAction
  | RemoveSelectedAction
  | RemoveAllAction
  | SliceEndAction
  | ToggleSelectAllAction
  | UpdateRowsAction
  | RemoveRowsAction
  | FilterUpsertAction
  | FiltersClearAllAction;

export type LotsFilterReducerActionType = FilterUpsertAction | FiltersClearAllAction;

export type LotRowID = string;

export interface LotRowData extends ClientLotHydrated {
  rowId?: LotRowID;
  rowSelected?: boolean;
  allowedCommunityPlanIds?: string[];
  selectedLocationPlanNames?: string[];
  /** Encoded id of this lot's phase-level ancestor (or self), for the map's phase filter. */
  phaseLocationId?: string | null;
  /** Encoded id of this lot's product-line-level ancestor (or self), for the map's product line filter. */
  productLineLocationId?: string | null;
}
export type EmptyLot = {};
export type LotsReducerType = (lots: LotRowData[], action: LotsReducerActionType) => LotRowData[];
export type FiltersReducerType = (filters: FilterValuesType, action: LotsFilterReducerActionType) => LotRowData[];
export type FilterValuesType = string[];
export type FilterTupleType = [string, FilterValuesType];
export type FilterPhaseOption = { value: string; label: string };
export type FilterBlockOption = { value: string; label: string };
export type LotActionType =
  | "reset"
  | "insert"
  | "insert_multiple"
  | "insert_empty_row"
  | "upsert"
  | "remove_one"
  | "remove_selected"
  | "remove_all"
  | "toggle_select_all"
  | "update_rows"
  | "remove_rows"
  | "slice_end"
  | "filter_upsert"
  | "clear_all";

// These map directly to the corresponding fields in LotRowData, used in filterLots to get the prop value from the lots.
// Unannotated on purpose: `Array<keyof LotRowData>` would widen `LotFilterKey` to every lot field.
export const lotFilterKeys = [
  "lotStatus",
  "lotTypes",
  "spec",
  "phase",
  "block",
  "area",
  "depth",
  "width",
  "releasedAt",
  "allowedCommunityPlanIds",
  "phaseLocationId",
  "productLineLocationId",
] as const satisfies readonly (keyof LotRowData)[];
export type LotFilterKey = (typeof lotFilterKeys)[number];
export const lotNullableFilterKeys = [
  "area",
  "depth",
  "width",
  "phaseLocationId",
  "productLineLocationId",
] as const satisfies readonly (keyof LotRowData)[];
export type LotNullableFilterKey = (typeof lotNullableFilterKeys)[number];

export interface LotDetailsContextProps {
  builderId: string;
  community: ClientCommunityWithLevel;
  inheritedAddress: EffectiveLocationAddress | null;
  displayPrices: boolean;
  lot: LotRowData;
}

export interface LotsContextProps {
  builderId: string | undefined;
  communityId: string | undefined;
  communityName?: string;
  location?: ClientLocationWithLevel;
  dispatch?: React.Dispatch<LotsReducerActionType>;
  lotsState?: any[];
  lotCount?: number;
  addLot?: (lot: LotRowData) => void;
  addLots?: (lots: LotRowData[]) => void;
  addNewLots?: (lots: EmptyLot[]) => void;
  setLotCount?: (cnt: number) => void;
  getNextAvailableLotNumber?: () => number;
  getRow?: (rowId: LotRowID) => LotRowData;
  updateCellValue: (
    rowId: LotRowID,
    fieldName: keyof LotRowData,
    fieldValue: string | boolean | number | FilterValuesType | LotRowID,
  ) => void;
  updateSelected: (
    fieldName: keyof LotRowData,
    fieldValue: keyof LotRowData | boolean | string | number | FilterValuesType | LotRowID | null,
    rows: LotRowID[],
  ) => void;
  removeLot?: (rowId: LotRowID) => void;
  removeSelected?: (activeRowId?: LotRowID) => void;
  removeAll?: () => void;
  filtersState?: LotFilterRow[];
  updateFilter?: (arg1: string, arg2: string[]) => void;
  clearAllFilters?: () => void;
  getFilter?: (name: string) => FilterValuesType;
  getActiveFilterNames?: () => FilterValuesType;
  getFilterCount?: () => number;
  closeLotMenus?: boolean;
  setCloseLotMenus: (arg1: boolean) => void;
  getSelectedRows: () => LotRowData[];
  getSelectedCount: () => number;
  getUniquePhases?: () => FilterPhaseOption[];
  getUniqueBlocks?: () => FilterBlockOption[];
  toggleSelectAll?: (selected: boolean, locationId: string, rowIds?: LotRowID[]) => void;
  handleUploadSuccess?: () => void;
  handleUploadFailure?: (msg: UserNotificationType) => void;
  setOpenCsvUploadModal?: React.Dispatch<SetStateAction<boolean>>;
}

/** Row ids for the row set `updateSelected` takes. */
export function rowIdsOf(lots: Array<Pick<LotRowData, "rowId">>): LotRowID[] {
  return lots.map((lot) => ensureDefined(lot.rowId));
}

export function getAllowedCommunityPlanIdsFromLot(lot: ClientLotHydrated): string[] {
  return lot.lotCommunityPlans.map((lcp) => lcp.communityPlanId);
}

export function communityPlanAllowedOnLot(lot: ClientLotHydrated, communityPlanId: string): boolean {
  return lot.lotCommunityPlans.some((lcp) => lcp.communityPlanId === communityPlanId);
}

export const lotRowIndexFn = <T extends { lotNumber: string; block?: string | null; phase?: string | null }>(arg: T) =>
  `LN-${arg.lotNumber}:B-${arg.block ?? ""}:P-${arg.phase ?? ""}`;

const LotsContext = createContext<LotsContextProps | null>(null);
LotsContext.displayName = "LotsContext";

export const LotDetailsContext = createContext<LotDetailsContextProps | null>(null);
LotDetailsContext.displayName = "LotDetailsContext";

export function useLotDetailsContext(): LotDetailsContextProps {
  const context = useContext(LotDetailsContext);
  if (!context) {
    throw new Error("useLotDetailsContext must be used within a LotDetailsContext.Provider");
  }
  return context;
}

export default LotsContext;
